import React, { useMemo } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { LinkIcon, SparklesIcon, HeartIcon, BookOpenIcon } from '@heroicons/react/24/outline';

const ResourcesSection = () => {
  const reduceMotion = useReducedMotion();
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.12 });

  const links = useMemo(() => [
    {
      title: 'TelefonSeelsorge',
      url: 'https://www.telefonseelsorge.de',
      description: '24/7 erreichbar: 0800 111 0 111 oder 0800 111 0 222',
      badge: '24/7',
      category: 'sofort',
    },
    {
      title: 'Nummer gegen Kummer',
      url: 'https://www.nummergegenkummer.de',
      description: 'Kinder- und Jugendtelefon: 116 111',
      badge: '116 111',
      category: 'sofort',
    },
    {
      title: 'Hilfetelefon Gewalt gegen Frauen',
      url: 'https://www.hilfetelefon.de',
      description: '24/7 erreichbar: 08000 116 016',
      badge: '24/7',
      category: 'sofort',
    },
    {
      title: 'Frauenschutzhaus Dresden e.V.',
      url: 'https://www.fsh-dresden.de/',
      description: 'Beratung und Hilfe bei häuslicher Gewalt',
      badge: 'Regional',
      category: 'support',
    },
    {
      title: 'Weißer Ring',
      url: 'https://weisser-ring.de',
      description: 'Hilfe für Opfer von Kriminalität',
      badge: 'Opferhilfe',
      category: 'support',
    },
    {
      title: 'SG – Systemische Gesellschaft',
      url: 'https://systemische-gesellschaft.de/',
      description: 'Fachverband für systemische Therapie & Beratung',
      badge: 'Fachinfo',
      category: 'info',
    },
  ], []);

  const grouped = useMemo(() => ({
    sofort: links.filter((l) => l.category === 'sofort'),
    support: links.filter((l) => l.category === 'support'),
    info: links.filter((l) => l.category === 'info'),
  }), [links]);

  const sectionAnim = {
    hidden: { opacity: 0, y: 12 },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: reduceMotion ? 0 : 0.5,
        ease: 'easeOut',
        when: 'beforeChildren',
        staggerChildren: reduceMotion ? 0 : 0.05,
      },
    },
  };

  const itemAnim = {
    hidden: { opacity: 0, y: reduceMotion ? 0 : 8 },
    show: { opacity: 1, y: 0, transition: { duration: reduceMotion ? 0 : 0.24, ease: 'easeOut' } },
  };

  const hoverFx = (el, on) => {
    if (reduceMotion || !el) return;
    el.style.transform = on ? 'translateY(-2px)' : 'translateY(0px)';
    el.style.boxShadow = on ? 'var(--shadow-md)' : 'var(--shadow-sm)';
    el.style.borderColor = on ? 'var(--color-border-strong)' : 'var(--color-border-subtle)';
  };

  const Badge = ({ children }) => (
    <span
      className="text-[11px] px-2 py-1"
      style={{
        borderRadius: 999,
        backgroundColor: 'var(--color-surface-2)',
        border: '1px solid var(--color-border-subtle)',
        color: 'var(--color-text-muted)',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </span>
  );

  const CardLink = ({ link }) => (
    <motion.a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      variants={itemAnim}
      className="surface block focus:outline-none overflow-hidden"
      style={{
        borderRadius: 'var(--radius-xl)',
        padding: 14,
        transition: reduceMotion ? 'none' : 'transform 120ms ease, box-shadow 120ms ease, border-color 120ms ease',
      }}
      onMouseEnter={(e) => hoverFx(e.currentTarget, true)}
      onMouseLeave={(e) => hoverFx(e.currentTarget, false)}
    >
      <div className="flex items-start gap-3 min-w-0">
        <span className="text-xl mt-0.5" aria-hidden="true">
          {link.icon}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <h4 className="font-semibold flex items-center gap-2 min-w-0" style={{ color: 'var(--color-text-primary)' }}>
              <span className="truncate">{link.title}</span>
              <LinkIcon className="w-4 h-4 shrink-0" style={{ opacity: 0.55, color: 'var(--color-text-muted)' }} />
            </h4>
            {link.badge ? <Badge>{link.badge}</Badge> : null}
          </div>
          <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            {link.description}
          </p>
        </div>
      </div>
    </motion.a>
  );

  return (
    <section id="resources" className="py-14">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div ref={ref} variants={sectionAnim} initial="hidden" animate={inView ? 'show' : 'hidden'}>
          {/* Header */}
          <div className="text-center mb-10">
            <h2 className="font-serif text-3xl md:text-4xl mb-3" style={{ color: 'var(--color-text-primary)' }}>
              Hilfe & Links
            </h2>
            <p className="max-w-2xl mx-auto" style={{ color: 'var(--color-text-secondary)' }}>
              Soforthilfe, Unterstützung und Orientierung.
            </p>
            <div className="section-divider" />
          </div>

          {/* 2×3 Grid */}
          <div className="grid lg:grid-cols-2 gap-6">
            {grouped.sofort.map((link) => (
              <CardLink key={link.title} link={link} />
            ))}
            {grouped.support.map((link) => (
              <CardLink key={link.title} link={link} />
            ))}
            {grouped.info.map((link) => (
              <CardLink key={link.title} link={link} />
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ResourcesSection;